"use client";

import PublicHeader from "@/components/PublicHeader";
import Footer from "@/components/Footer";
import {
  PageContainer,
  ContentContainer,
  Title,
  LastUpdated,
  Section,
  SectionTitle,
  SubsectionTitle,
  Paragraph,
  List,
} from "./page.styles";

export default function PrivacyPage() {
  return (
    <PageContainer>
      <PublicHeader />
      <ContentContainer>
        <Title>Privacy Policy</Title>
        <LastUpdated>Last updated March 08, 2023</LastUpdated>

        <Section>
          <SectionTitle>1. Introduction</SectionTitle>
          <Paragraph>
            Welcome to Drizl (&quot;we,&quot; &quot;our,&quot; or
            &quot;us&quot;). We respect your privacy and are committed to
            protecting your personal information. This Privacy Policy explains
            how we collect, use, disclose, and safeguard your information when
            you use our Platform.
          </Paragraph>
          <Paragraph>
            By using Drizl, you agree to the collection and use of information
            in accordance with this Privacy Policy. If you do not agree with our
            policies and practices, please do not use the Platform.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>2. Information We Collect</SectionTitle>

          <SubsectionTitle>2.1 Information You Provide</SubsectionTitle>
          <Paragraph>
            We collect information that you voluntarily provide when using our
            Platform:
          </Paragraph>
          <List>
            <li>
              <strong>Account Information:</strong> Name, email address, phone
              number, password
            </li>
            <li>
              <strong>Profile Information:</strong> Profile photo, bio, business
              name (for Artisans), delivery address
            </li>
            <li>
              <strong>Payment Information:</strong> Credit card details, billing
              address (processed securely through our payment partners)
            </li>
            <li>
              <strong>Meal Listings:</strong> Meal descriptions, photos,
              ingredients, pricing, allergen information
            </li>
            <li>
              <strong>Communications:</strong> Messages, reviews, ratings, and
              feedback
            </li>
          </List>

          <SubsectionTitle>
            2.2 Automatically Collected Information
          </SubsectionTitle>
          <Paragraph>
            When you use our Platform, we automatically collect certain
            information:
          </Paragraph>
          <List>
            <li>
              <strong>Device Information:</strong> IP address, browser type,
              device type, operating system
            </li>
            <li>
              <strong>Usage Data:</strong> Pages visited, time spent on pages,
              clicks, search queries
            </li>
            <li>
              <strong>Location Data:</strong> GPS coordinates (with your
              permission) for delivery and local meal discovery
            </li>
            <li>
              <strong>Cookies:</strong> Session data, preferences,
              authentication tokens
            </li>
          </List>

          <SubsectionTitle>2.3 Information from Third Parties</SubsectionTitle>
          <List>
            <li>
              Social media profile information (if you sign up via social login)
            </li>
            <li>Payment processing information from our payment partners</li>
            <li>Analytics data from third-party services</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>3. How We Use Your Information</SectionTitle>
          <Paragraph>
            We use the collected information for various purposes:
          </Paragraph>
          <List>
            <li>
              <strong>Service Provision:</strong> To create and manage your
              account, process orders, facilitate transactions
            </li>
            <li>
              <strong>Communication:</strong> To send order confirmations,
              updates, notifications, and customer support responses
            </li>
            <li>
              <strong>Personalization:</strong> To recommend meals, customize
              your experience, and show relevant content
            </li>
            <li>
              <strong>Safety & Security:</strong> To detect fraud, prevent
              abuse, and ensure platform safety
            </li>
            <li>
              <strong>Analytics:</strong> To understand usage patterns, improve
              our services, and develop new features
            </li>
            <li>
              <strong>Marketing:</strong> To send promotional emails, special
              offers, and newsletters (you can opt-out anytime)
            </li>
            <li>
              <strong>Legal Compliance:</strong> To comply with legal
              obligations and enforce our Terms & Conditions
            </li>
          </List>
        </Section>

        <Section>
          <SectionTitle>4. Information Sharing and Disclosure</SectionTitle>

          <SubsectionTitle>4.1 With Artisans and Consumers</SubsectionTitle>
          <Paragraph>
            When you place an order, we share necessary information with the
            Artisan (name, delivery address, phone number, order details).
            Artisans can view your public profile information.
          </Paragraph>

          <SubsectionTitle>4.2 With Service Providers</SubsectionTitle>
          <Paragraph>
            We share information with trusted third-party service providers who
            assist us in:
          </Paragraph>
          <List>
            <li>Payment processing (Stripe, PayPal)</li>
            <li>Cloud hosting and storage (Firebase, Google Cloud)</li>
            <li>Analytics and monitoring (Google Analytics, Sentry)</li>
            <li>Email communications (SendGrid, Mailchimp)</li>
            <li>Customer support tools</li>
          </List>

          <SubsectionTitle>4.3 For Legal Reasons</SubsectionTitle>
          <Paragraph>
            We may disclose your information if required to:
          </Paragraph>
          <List>
            <li>Comply with legal obligations, court orders, or subpoenas</li>
            <li>Protect our rights, property, or safety</li>
            <li>Investigate fraud or security issues</li>
            <li>Enforce our Terms & Conditions</li>
          </List>

          <SubsectionTitle>4.4 Business Transfers</SubsectionTitle>
          <Paragraph>
            In the event of a merger, acquisition, or sale of assets, your
            information may be transferred to the new entity. We will notify you
            of any such change.
          </Paragraph>

          <SubsectionTitle>4.5 With Your Consent</SubsectionTitle>
          <Paragraph>
            We may share your information with other third parties when you
            explicitly consent.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>5. Data Security</SectionTitle>
          <Paragraph>
            We implement appropriate technical and organizational measures to
            protect your information:
          </Paragraph>
          <List>
            <li>Encryption of data in transit and at rest</li>
            <li>Secure authentication and password hashing</li>
            <li>Regular security audits and vulnerability assessments</li>
            <li>Access controls and employee training</li>
            <li>Compliance with industry security standards</li>
          </List>
          <Paragraph>
            However, no method of transmission over the Internet is 100% secure.
            While we strive to protect your information, we cannot guarantee
            absolute security.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>6. Your Privacy Rights</SectionTitle>

          <SubsectionTitle>6.1 Access and Correction</SubsectionTitle>
          <Paragraph>
            You have the right to access and update your personal information at
            any time through your account settings.
          </Paragraph>

          <SubsectionTitle>6.2 Data Deletion</SubsectionTitle>
          <Paragraph>
            You can request deletion of your account and personal data by
            contacting us at privacy@drizl.app. We will delete your information
            within 30 days, except where we are required to retain it for legal
            or business purposes.
          </Paragraph>

          <SubsectionTitle>6.3 Marketing Opt-Out</SubsectionTitle>
          <Paragraph>
            You can unsubscribe from marketing emails by clicking the
            &quot;unsubscribe&quot; link in any promotional email or adjusting
            your notification preferences in account settings.
          </Paragraph>

          <SubsectionTitle>6.4 Cookie Management</SubsectionTitle>
          <Paragraph>
            You can control cookies through your browser settings. However,
            disabling cookies may limit your ability to use certain features of
            the Platform.
          </Paragraph>

          <SubsectionTitle>6.5 Do Not Track</SubsectionTitle>
          <Paragraph>
            We do not currently respond to &quot;Do Not Track&quot; browser
            signals.
          </Paragraph>

          <SubsectionTitle>
            6.6 California Privacy Rights (CCPA)
          </SubsectionTitle>
          <Paragraph>
            California residents have additional rights under the California
            Consumer Privacy Act:
          </Paragraph>
          <List>
            <li>Right to know what personal information is collected</li>
            <li>Right to know if personal information is sold or disclosed</li>
            <li>Right to opt-out of the sale of personal information</li>
            <li>Right to deletion of personal information</li>
            <li>Right to non-discrimination for exercising privacy rights</li>
          </List>
          <Paragraph>
            To exercise these rights, contact us at privacy@drizl.app.
          </Paragraph>

          <SubsectionTitle>6.7 European Privacy Rights (GDPR)</SubsectionTitle>
          <Paragraph>
            If you are in the European Economic Area, you have rights under GDPR
            including:
          </Paragraph>
          <List>
            <li>Right to access your personal data</li>
            <li>Right to rectification of inaccurate data</li>
            <li>Right to erasure (&quot;right to be forgotten&quot;)</li>
            <li>Right to restrict processing</li>
            <li>Right to data portability</li>
            <li>Right to object to processing</li>
            <li>Right to withdraw consent</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>7. Children&apos;s Privacy</SectionTitle>
          <Paragraph>
            Drizl is not intended for users under the age of 18. We do not
            knowingly collect personal information from children. If you believe
            we have collected information from a child, please contact us
            immediately at privacy@drizl.app.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>8. Data Retention</SectionTitle>
          <Paragraph>
            We retain your personal information for as long as necessary to:
          </Paragraph>
          <List>
            <li>Provide our services</li>
            <li>Comply with legal obligations</li>
            <li>Resolve disputes</li>
            <li>Enforce our agreements</li>
          </List>
          <Paragraph>
            After account deletion, we may retain certain information in
            anonymized or aggregated form for analytics and legal purposes.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>9. International Data Transfers</SectionTitle>
          <Paragraph>
            Your information may be transferred to and processed in countries
            other than your own. We ensure appropriate safeguards are in place
            to protect your information in accordance with this Privacy Policy
            and applicable data protection laws.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>10. Third-Party Links</SectionTitle>
          <Paragraph>
            Our Platform may contain links to third-party websites or services.
            We are not responsible for the privacy practices of these third
            parties. We encourage you to review their privacy policies before
            providing any information.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>11. Changes to This Privacy Policy</SectionTitle>
          <Paragraph>
            We may update this Privacy Policy from time to time. We will notify
            you of significant changes by email or through a prominent notice on
            the Platform. Your continued use after such changes constitutes
            acceptance of the updated policy.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>12. Contact Us</SectionTitle>
          <Paragraph>
            If you have questions, concerns, or requests regarding this Privacy
            Policy or your personal information, please contact us:
          </Paragraph>
          <Paragraph>
            <strong>Drizl, LLP</strong>
            <br />
            Glen Arm, MD 21057
            <br />
            United States
            <br />
            <br />
            <strong>Email:</strong> support@drizl.app
          </Paragraph>
        </Section>
      </ContentContainer>
      <Footer />
    </PageContainer>
  );
}
